import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Collapse,
  alpha,
  useTheme
} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

const FormulationsPermissions = ({ 
  permissions, 
  roleForm, 
  setRoleForm 
}) => {
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = useState({
    special_permissions: false,
    formulations_page: false,
    formulations_overview: false,
    formulations_ingredients: false,
    formulations_procedures: false,
    formulations_tests: false,
    formulations_costs: false,
    formulations_summary: false
  });
  
  // Formulation pages mapping
  const formulationPages = ['formulations_page', 'formulations_overview', 'formulations_ingredients', 'formulations_procedures', 'formulations_tests', 'formulations_costs', 'formulations_summary'];
  
  const pageNameMapping = {
    'formulations_page': 'Formulations Page',
    'formulations_overview': 'Formulation Overview Page',
    'formulations_ingredients': 'Formulation Ingredients Page',
    'formulations_procedures': 'Formulation Procedures Page',
    'formulations_tests': 'Formulation Tests Page',
    'formulations_costs': 'Formulation Costs Page',
    'formulations_summary': 'Formulation Summary Page'
  };

  // Special Permissions Section
  const renderSpecialPermissions = () => {
    const isExpanded = expandedSections.special_permissions !== false;
    const createPermission = permissions.find(p => p.name === 'products.formulations.create');
    const approvePermission = permissions.find(p => p.name === 'products.formulations.approve');
    const deletePermission = permissions.find(p => p.name === 'products.formulations.delete_permanent');
    
    if (!createPermission && !approvePermission && !deletePermission) return null;

    return (
      <Box sx={{ mb: 2 }}>
        <Box
          onClick={() => setExpandedSections(prev => ({ ...prev, special_permissions: !isExpanded }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.grey[400], 0.3)}` : 'none',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
            borderRadius: 2,
            overflow: 'hidden',
            background: 'white',
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.03)})`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ScienceIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              Special Formulation Permissions
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isExpanded ? '▼' : '▶'}
          </Typography>
        </Box>

        <Collapse in={isExpanded}>
          <Box sx={{ p: 2, border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`, borderTop: 'none', borderRadius: '0 0 8px 8px', background: 'white' }}>
            {createPermission && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: (approvePermission || deletePermission) ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Create Formulations
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can create new formulations
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[createPermission.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[createPermission.id]) {
                      newPermissions[createPermission.id] = {};
                    }
                    newPermissions[createPermission.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}
            {approvePermission && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: deletePermission ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Approve Formulations
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can approve formulations
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[approvePermission.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[approvePermission.id]) {
                      newPermissions[approvePermission.id] = {};
                    }
                    newPermissions[approvePermission.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}
            {deletePermission && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Permanently Delete Formulations
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can permanently delete formulations
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[deletePermission.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[deletePermission.id]) {
                      newPermissions[deletePermission.id] = {};
                    }
                    newPermissions[deletePermission.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  const renderFormulationPagePermissions = (pageKey, pageName) => {
    const isExpanded = expandedSections[pageKey] !== false;
    
    let pagePermissions = [];
    
    if (pageKey === 'formulations_page') {
      // For the main formulations page, get the main formulations permissions
      pagePermissions = permissions.filter(p => 
        p.name === 'products.formulations.view' || 
        p.name === 'products.formulations.edit' ||
        p.name === 'products.formulations.delete'
      ) || [];
    } else {
      const tabName = pageKey.replace('formulations_', '');
      pagePermissions = permissions.filter(p => 
        p.name?.includes(`products.formulations.${tabName}`)
      ) || [];
    }

    if (pagePermissions.length === 0) return null;

    const viewPerm = pagePermissions.find(p => p.name?.includes('.view'));
    const editPerm = pagePermissions.find(p => p.name?.includes('.edit'));

    return (
      <Box 
        key={pageKey}
        sx={{ 
          mb: 2,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
          borderRadius: 2,
          overflow: 'hidden',
          background: 'white'
        }}
      >
        {/* Page Header */}
        <Box
          onClick={() => setExpandedSections(prev => ({ ...prev, [pageKey]: !isExpanded }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.grey[400], 0.3)}` : 'none',
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.03)})`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ScienceIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {pageName}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isExpanded ? '▼' : '▶'}
          </Typography>
        </Box>

        {/* Permission Switches */}
        <Collapse in={isExpanded}>
          <Box sx={{ p: 2 }}>
            {/* View Permission */}
            {viewPerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: editPerm ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    View
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can view {pageName.toLowerCase()}
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[viewPerm.id]?.read || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[viewPerm.id]) {
                      newPermissions[viewPerm.id] = {};
                    }
                    newPermissions[viewPerm.id].read = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Edit Permission */}
            {editPerm && (pageKey === 'formulations_page' || pageKey === 'formulations_overview' || pageKey === 'formulations_ingredients' || pageKey === 'formulations_procedures' || pageKey === 'formulations_tests' || pageKey === 'formulations_costs') && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Edit
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can edit {pageName.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[editPerm.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[editPerm.id]) {
                      newPermissions[editPerm.id] = {};
                    }
                    newPermissions[editPerm.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      {renderSpecialPermissions()}
      {formulationPages.map(pageKey => {
        const pageName = pageNameMapping[pageKey] || pageKey;
        return renderFormulationPagePermissions(pageKey, pageName);
      })}
    </Box>
  );
};

export default FormulationsPermissions;
